interface IEditableColumn {
  key?: string
  label?: string
}

interface IColumnSizingOptions {
  minWidth?: number
  maxWidth?: number
  pixelsPerCharacter?: number
  basePadding?: number
}

const DEFAULT_MIN_WIDTH = 130
const DEFAULT_MAX_WIDTH = 300
const DEFAULT_PIXELS_PER_CHARACTER = 8
const DEFAULT_BASE_PADDING = 52

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const getTextLength = (value: unknown): number => {
  if (value === null || value === undefined) return 0
  return String(value).trim().length
}

export const getAdaptiveColumnWidths = <TColumn extends IEditableColumn, TRow extends object>(
  columns: TColumn[],
  items: TRow[],
  options: IColumnSizingOptions = {},
): Record<string, number> => {
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH
  const pixelsPerCharacter = options.pixelsPerCharacter ?? DEFAULT_PIXELS_PER_CHARACTER
  const basePadding = options.basePadding ?? DEFAULT_BASE_PADDING

  return columns.reduce<Record<string, number>>((result, column, columnIndex) => {
    const key = column.key || `column_${columnIndex}`
    const headerLength = getTextLength(column.label || key)
    const maxValueLength = items.reduce((currentMax, item) => {
      const row = item as Record<string, unknown>
      return Math.max(currentMax, getTextLength(row[key]))
    }, 0)

    const contentLength = Math.max(headerLength, maxValueLength)
    result[key] = clamp(contentLength * pixelsPerCharacter + basePadding, minWidth, maxWidth)
    return result
  }, {})
}

export const shouldShowCellTooltip = (value: string, width: number): boolean => {
  if (!value) return false

  const estimatedVisibleCharacters = Math.max(Math.floor((width - 48) / DEFAULT_PIXELS_PER_CHARACTER), 8)
  return value.length > estimatedVisibleCharacters
}
